// LiveRegion + useLiveAnnounce — single app-level aria-live=polite region
// for ephemeral announcements (toast outcomes, mutation success/failure,
// route-change titles for screen readers).
//
// Plan ref: 06-02-PLAN §Task 1 step 5 + step 8 (centralize at app root)
//          + 06-RESEARCH-motion-responsive-a11y.md §5 (a11y) §Open Q3
//          + WCAG 2.2 AA — 4.1.3 Status Messages
//
// Architecture:
//   - <LiveRegion /> is mounted ONCE at the app root, inside <ThemeProvider>.
//   - useLiveAnnounce() returns a function the caller invokes to push a
//     message; the latest message replaces the prior one (aria-atomic=true
//     ensures the entire region is re-read by AT, not just the diff).
//   - After 5s the message clears so screen readers don't re-announce it
//     when the user re-focuses the region.
//
// Implementation:
//   - Module-level Set of subscribers (the LiveRegion mounted in the tree).
//   - useLiveAnnounce reads the singleton store; LiveRegion subscribes to it.
//   - This avoids requiring a Context provider at every mount point — useful
//     for tests that render <LiveRegion /> + a hook consumer side-by-side.

import { useEffect, useState, useSyncExternalStore } from 'react';

type Listener = (message: string) => void;

const listeners = new Set<Listener>();
let currentMessage = '';

function announce(message: string): void {
  currentMessage = message;
  listeners.forEach((l) => l(message));
}

function subscribe(listener: () => void): () => void {
  const wrapped: Listener = () => listener();
  listeners.add(wrapped);
  return () => {
    listeners.delete(wrapped);
  };
}

function getSnapshot(): string {
  return currentMessage;
}

function getServerSnapshot(): string {
  return '';
}

/**
 * Returns a stable announce(message) function. Calling it pushes the message
 * into the app-level <LiveRegion> for screen-reader playback.
 */
export function useLiveAnnounce(): (message: string) => void {
  return announce;
}

/**
 * App-root live region. Mount ONCE inside <ThemeProvider> in main.tsx.
 * Renders an aria-live=polite region; useLiveAnnounce() pushes messages.
 */
export function LiveRegion(): JSX.Element {
  const message = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const [visible, setVisible] = useState<string>(message);

  // Sync the displayed message to the latest store value, then clear after
  // 5s so AT doesn't re-announce on re-focus.
  useEffect(() => {
    setVisible(message);
    if (!message) return;
    const id = window.setTimeout(() => {
      setVisible('');
      // Reset the store so a future re-mount doesn't replay the last message.
      currentMessage = '';
    }, 5_000);
    return () => window.clearTimeout(id);
  }, [message]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-live-region=""
      className="sr-only"
    >
      {visible}
    </div>
  );
}
